/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import { Component, Model, RealObject } from '@hatiolab/things-scene'
import * as THREE from 'three'

const STOCK_COLOR = '#ccaa76'

function createStockMaterial(color: string | number, opts?: { opacity?: number; transparent?: boolean }): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    color,
    side: THREE.FrontSide,
    metalness: 0,
    roughness: 1,
    envMapIntensity: 0
  })
  if (opts?.opacity !== undefined) {
    mat.opacity = opts.opacity
    mat.transparent = opts.transparent ?? true
  }
  return mat
}

/**
 * Stock material/legend 정보를 제공하는 컴포넌트 인터페이스.
 * Visualizer와 RackTable 모두 이를 구현한다.
 */
export interface StockMaterialProvider {
  _stock_materials: THREE.Material[]
  _default_material?: THREE.Material
  _empty_material?: THREE.Material
  legendTarget?: Component
  hideEmptyStock?: boolean
}

export class Stock extends RealObject<THREE.Mesh> {
  static defaultMaterial = createStockMaterial(STOCK_COLOR)
  static defaultEmptyMaterial = createStockMaterial(STOCK_COLOR, { opacity: 0.33, transparent: true })
  static stockGeometry = new THREE.BoxGeometry(1, 1, 1)

  _hideEmptyStock: boolean = false
  _focused: boolean = false
  declare _focusedAt?: number

  model: any

  constructor(component: Component, model: any) {
    super(component)
    this.model = model
  }

  protected getObject3dInstance() {
    return new THREE.Mesh()
  }

  /**
   * 가장 가까운 StockMaterialProvider 조상을 찾는다 (RackTable 또는 Visualizer).
   */
  get provider(): StockMaterialProvider | undefined {
    let component = this.component

    while (component) {
      if ('_stock_materials' in component) {
        return component as unknown as StockMaterialProvider
      }
      component = component.parent
    }
  }

  private get _legendStatus(): any | undefined {
    return this.provider?.legendTarget?.getState('status') || undefined
  }

  getMaterial(index: number) {
    const status = this._legendStatus
    if (!status) return this.userDefineDefaultMaterial

    const range = status.ranges?.[index]
    if (!(range && range.color)) {
      this.stockMaterials[index] = this.userDefineDefaultMaterial
    }

    if (!this.stockMaterials[index]) {
      this.stockMaterials[index] = createStockMaterial(range.color)
    }

    var alpha = range.color.replace(/^.*,(.+)\)/, '$1')
    if (alpha > 0 && alpha < 1) {
      this.stockMaterials[index].opacity = alpha
      this.stockMaterials[index].transparent = true
    }

    return this.stockMaterials[index]
  }

  get stockMaterials(): THREE.Material[] {
    return this.provider?._stock_materials ?? []
  }

  get userDefineDefaultMaterial(): THREE.Material {
    const p = this.provider

    if (p) {
      if (!p._default_material) {
        const status = this._legendStatus
        const defaultColor = status?.defaultColor
        if (!defaultColor) return Stock.defaultMaterial

        var alpha = Number(defaultColor.replace(/^.*,(.+)\)/, '$1'))
        var hasAlpha = alpha > 0 && alpha < 1
        p._default_material = createStockMaterial(defaultColor, hasAlpha ? { opacity: alpha, transparent: true } : undefined)
      }
      return p._default_material
    }

    return Stock.defaultMaterial
  }

  get emptyMaterial(): THREE.Material {
    const p = this.provider

    if (p) {
      if (!p._empty_material) {
        const status = this._legendStatus
        const defaultColor = status?.defaultColor || STOCK_COLOR

        var alpha = Number(defaultColor.replace(/^.*,(.+)\)/, '$1'))
        var opacity = (alpha > 0 && alpha < 1) ? alpha : 0.33
        p._empty_material = createStockMaterial(defaultColor, { opacity, transparent: true })
      }
      return p._empty_material
    }

    return Stock.defaultEmptyMaterial
  }

  build() {
    super.build()

    var { width, height, depth } = this.model

    this._hideEmptyStock = !!this.provider?.hideEmptyStock

    this.createStock(width, height, depth)
  }

  createStock(w: number, h: number, d: number) {
    this.object3d.geometry = Stock.stockGeometry
    this.object3d.material = this._hideEmptyStock ? this.emptyMaterial : this.userDefineDefaultMaterial

    this.object3d.scale.set(w, d, h)

    this.object3d.receiveShadow = true

    this.object3d.castShadow = true
    this.object3d.onBeforeRender = () => {
      this.onBeforeRender()
    }
  }

  onchangeStockData(data: any) {
    this.object3d.userData = {
      ...this.object3d.userData,
      data
    }

    const stockStatus = this._legendStatus as
      | { field: string; aggregation?: string; ranges: { min?: string | number; max?: string | number }[] }
      | undefined
    if (!stockStatus) return

    var statusField = stockStatus.field
    var aggregation = stockStatus.aggregation || 'sum'
    var ranges = stockStatus.ranges

    if (!(statusField && ranges)) return

    var items = data.items

    if (!items) {
      this.object3d.material = this._hideEmptyStock ? this.emptyMaterial : this.userDefineDefaultMaterial
      return
    }

    // Collect all status values from items
    var statusValues: any[] = items
      .map((item: any) => item && item[statusField])
      .filter((value: any) => value !== undefined && value !== null)

    if (statusValues.length === 0) {
      this.object3d.material = this._hideEmptyStock ? this.emptyMaterial : this.userDefineDefaultMaterial
      return
    }

    // Apply aggregation function
    var status: number
    switch (aggregation) {
      case 'sum':
        status = statusValues.reduce((sum: number, val: any) => sum + Number(val), 0)
        break
      case 'avg':
        status = statusValues.reduce((sum: number, val: any) => sum + Number(val), 0) / statusValues.length
        break
      case 'min':
        status = Math.min(...statusValues.map((val: any) => Number(val)))
        break
      case 'max':
        status = Math.max(...statusValues.map((val: any) => Number(val)))
        break
      default:
        status = statusValues.reduce((sum: number, val: any) => sum + Number(val), 0)
    }

    var isInRanges = ranges.some((range, index) => {
      let { min: minValue, max: maxValue } = range

      var min: number | undefined =
        minValue !== undefined && minValue !== null && minValue !== '' ? Number(minValue) : undefined
      var max: number | undefined =
        maxValue !== undefined && maxValue !== null && maxValue !== '' ? Number(maxValue) : undefined

      // If max is not set, treat it as infinity
      var maxCheck = max === undefined ? true : max > status
      var minCheck = min === undefined ? true : min <= status

      if (maxCheck && minCheck) {
        this.object3d.material = this.getMaterial(index)
        return true
      } else {
        this.object3d.material = this._hideEmptyStock ? this.emptyMaterial : this.userDefineDefaultMaterial
        return false
      }
    })
    if (!isInRanges) {
      this.object3d.material = this.userDefineDefaultMaterial
    }
  }

  onBeforeRender = () => {
    if (this._focused) {
      var lastTime = performance.now() - this._focusedAt!
      var progress = lastTime / 2000

      this.object3d.rotation.y = 2 * Math.PI * progress
      this.component.invalidate()
    } else if (this._focusedAt) {
      delete this._focusedAt
      this.object3d.rotation.y = 0
      this.component.invalidate()
    }
  }

  onmouseup(e: MouseEvent, _context: unknown, callback: (arg: { data: any; location: string }) => void) {
    if (!this.object3d.visible) return

    if (!this.object3d.userData || !this.object3d.userData.data)
      this.object3d.userData = {
        ...this.object3d.userData,
        data: {
          loc: this.object3d.name,
          items: [
            {
              loc: this.object3d.name
            }
          ]
        }
      }

    if (callback && typeof callback == 'function') {
      callback({
        ...this.object3d.userData.data,
        color: '#' + (this.object3d.material as any).color?.getHexString()
      })
    }
  }

  // all update functions should be intentionally empty. important!
  updateTransform() {}
  updateDimension() {}
  updateAlpha() {}
  updateFillStyle() {}
  updateStrokeStyle() {}
  updateHidden() {}
  updateText() {}
}
